import * as Sentry from '@sentry/react';
import { IsomorphicHelpers } from './IsomorphicHelpers';
import { IAppConfig } from '../reducers/pageData';

declare const window: Window & {
    appState?: {
        pageData?: {
            appConfig?: IAppConfig & { sentryDSN?: string };
        };
    };
};

// LandingPage and SearchPage are migrated to /frontend, so any render here means routing is wrong.
// Dedicated client so these events bypass the global error sampleRate (0.01 on production)
let hub: Sentry.Hub | null = null;

const getHub = (): Sentry.Hub | null => {
    if (hub) return hub;

    const appConfig = window.appState?.pageData?.appConfig;
    if (!appConfig?.sentryDSN || appConfig.environment === 'development') return null;

    hub = new Sentry.Hub(
        new Sentry.BrowserClient({
            dsn: appConfig.sentryDSN,
            environment: appConfig.environment,
            release: appConfig.buildVersion,
            sampleRate: 1,
        }),
    );
    hub.setTags({
        component: 'MOBILE_FRONTEND',
        brand: appConfig.portalId,
    });

    return hub;
};

// Rate limit: report each page at most once per browser session
const reportedPages = new Set<string>();

const wasReported = (page: string): boolean => {
    const storageKey = `legacyPageRenderReported:${page}`;
    if (reportedPages.has(page)) return true;
    reportedPages.add(page);

    try {
        if (window.sessionStorage.getItem(storageKey)) return true;
        window.sessionStorage.setItem(storageKey, '1');
    } catch (e) {
        // sessionStorage blocked (private mode etc.), in-memory set still limits per page load
    }

    return false;
};

export const capturePageRender = (page: string) => {
    if (IsomorphicHelpers.isServerSide || wasReported(page)) return;

    const pageHub = getHub();
    if (!pageHub) return;

    pageHub.withScope((scope) => {
        scope.setTag('page', page);
        scope.setTag('legacy_page_render', 'true');
        scope.setExtra('url', window.location.href);
        scope.setExtra('referrer', document.referrer);
        pageHub.captureMessage(
            `Legacy mobile ${page} rendered - should be served by /frontend`,
            Sentry.Severity.Warning,
        );
    });
};
